import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HERO_IMAGE, holidayPackages, popularVisas, testimonials, stats, domesticDestinations, internationalDestinations, popularDestinations } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import {
  Search, Star, ChevronLeft, ChevronRight, CalendarIcon, MapPin, Users,
  Ticket, Hotel, FileCheck, Landmark, BookUser, ShieldCheck, Send, Quote, ArrowRight, Phone, Mail, Clock,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { format } from "date-fns";

const formatINR = (n) => new Intl.NumberFormat("en-IN").format(n);

export default function Home() {
  const { toast } = useToast();
  const [packageType, setPackageType] = useState("international");
  const [destination, setDestination] = useState("");
  const [date, setDate] = useState(new Date());
  const [travellers, setTravellers] = useState("2 Adults");
  const [pkgIdx, setPkgIdx] = useState(0);
  const [visaIdx, setVisaIdx] = useState(0);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", city: "", travel: "", service: "", message: "",
  });

  const handleSearch = () => {
    toast({
      title: `${packageType === "domestic" ? "Domestic" : "International"} tour packages`,
      description: `Destination: ${destination || "Anywhere"} · ${format(date, "MMM dd, yyyy")}`,
    });
    // Scroll to holiday packages section
    const el = document.getElementById("packages");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollPkg = (dir) => {
    const el = document.getElementById("pkg-scroller");
    if (!el) return;
    const w = el.clientWidth * 0.85;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };
  const scrollVisa = (dir) => {
    const el = document.getElementById("visa-scroller");
    if (!el) return;
    const w = el.clientWidth * 0.85;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.city || !form.travel || !form.service) {
      toast({ title: "Please fill all required fields", description: "All starred fields are required.", variant: "destructive" });
      return;
    }
    toast({ title: "Enquiry received!", description: "Our travel expert will get back within 24 hours." });
    setForm({ name: "", phone: "", email: "", city: "", travel: "", service: "", message: "" });
  };

  return (
    <main>
      {/* HERO with embedded Search */}
      <section className="relative pt-20">
        <div className="relative min-h-[100vh] w-full overflow-hidden">
          <img src={HERO_IMAGE} alt="Aerial mountain view" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/20 to-white/80" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 flex flex-col items-center text-center">
            <span className="fade-up inline-block text-xs tracking-[0.35em] font-semibold bg-white/80 backdrop-blur px-4 py-1.5 rounded-full mb-6">
              <span className="text-[#70a308]">KITES</span> <span className="text-[#416200]">FLY</span> <span className="text-slate-500">• HOLIDAYS, VISAS AND EVERYTHING IN BETWEEN</span>
            </span>
            <h1 className="fade-up font-display text-5xl md:text-7xl lg:text-8xl font-bold text-slate-800 leading-tight">
              Your Next Trip, <span className="bg-gradient-to-r from-[#84c009] to-[#4f7700] bg-clip-text text-transparent italic">Perfectly Planned.</span>
            </h1>
            <p className="fade-up mt-5 text-lg md:text-xl text-slate-700 max-w-2xl">
              Your complete travel partner for holidays, flights, visas &amp; more.
            </p>

            <div className="fade-up mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => document.getElementById("trip-search")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-semibold text-white bg-gradient-to-r from-[#84c009] to-[#4f7700] hover:from-[#70a308] hover:to-[#416200] shadow-md transition-colors"
              >
                Plan Your Trip <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-semibold text-slate-800 bg-white/90 border border-[#ddedba] hover:bg-white transition-colors"
              >
                Explore Packages
              </Link>
            </div>

            {/* Trip Search Widget */}
            <div id="trip-search" className="fade-up mt-10 w-full max-w-4xl text-left">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl shadow-[#354d04]/15 border border-white p-5 md:p-7">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">PLAN YOUR TRIP</div>
                  <div className="flex items-center gap-2 bg-slate-50 rounded-full px-3 py-1.5">
                    <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}</div>
                    <span className="text-xs font-semibold text-slate-700">5.0</span>
                    <span className="text-xs text-slate-400">12 Google Reviews</span>
                  </div>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-800 mt-1 mb-5">Where would you like to go?</h3>

                <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr_auto] gap-4 items-end">
                  <div>
                    <label className="block text-[11px] tracking-widest text-slate-500 font-semibold mb-1.5">DESTINATION</label>
                    <Select value={destination} onValueChange={setDestination}>
                      <SelectTrigger className="h-11 rounded-xl text-left">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#70a308] shrink-0" />
                          <SelectValue placeholder="Select Destination" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {[...domesticDestinations, ...internationalDestinations].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-widest text-slate-500 font-semibold mb-1.5">TRAVEL DATE</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="w-full h-11 rounded-xl border border-input bg-background px-3 text-left flex items-center gap-2 hover:border-[#a6d24e] transition-colors">
                          <CalendarIcon className="w-4 h-4 text-[#70a308] shrink-0" />
                          <span className="text-sm text-slate-700">{format(date, "dd-MM-yyyy")}</span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-widest text-slate-500 font-semibold mb-1.5">TRAVELLERS</label>
                    <Select value={travellers} onValueChange={setTravellers}>
                      <SelectTrigger className="h-11 rounded-xl text-left">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-[#70a308] shrink-0" />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1 Adult">1 Adult</SelectItem>
                        <SelectItem value="2 Adults">2 Adults</SelectItem>
                        <SelectItem value="3 Adults">3 Adults</SelectItem>
                        <SelectItem value="4 Adults">4 Adults</SelectItem>
                        <SelectItem value="2 Adults, 1 Child">2 Adults, 1 Child</SelectItem>
                        <SelectItem value="2 Adults, 2 Children">2 Adults, 2 Children</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleSearch}
                    className="h-11 px-8 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold shadow-md"
                  >
                    <Search className="w-4 h-4 mr-2" /> Search
                  </Button>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                  <Link to="/contact" className="text-[#5c8606] font-semibold hover:underline text-sm">Book a meeting with our travel Agent →</Link>
                  <span className="text-xs text-slate-500">Rated 5.0 · 12 Google Reviews</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOLIDAY PACKAGES */}
      <section id="packages" className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">EXPLORE</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-800 mt-2">Holidays</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={() => scrollPkg(-1)} className="w-10 h-10 rounded-full border border-slate-200 hover:border-[#84c009] hover:text-[#5c8606] text-slate-600 flex items-center justify-center transition-colors" aria-label="Prev"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => scrollPkg(1)} className="w-10 h-10 rounded-full border border-slate-200 hover:border-[#84c009] hover:text-[#5c8606] text-slate-600 flex items-center justify-center transition-colors" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
        <div id="pkg-scroller" className="no-scrollbar flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4">
          {holidayPackages.map((p) => (
            <Card key={p.id} className="lift min-w-[300px] max-w-[300px] snap-start overflow-hidden rounded-2xl border-slate-100">
              <div className="relative h-44 overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                <Badge className="absolute top-3 left-3 bg-white text-slate-800 hover:bg-white rounded-full text-[11px]">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 mr-1" /> {p.rating}
                </Badge>
              </div>
              <CardContent className="p-5">
                <h4 className="font-bold text-slate-800 line-clamp-2 min-h-[48px]">{p.title}</h4>
                <p className="text-xs text-slate-500 mt-1">{p.duration}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {p.features.slice(0,4).map((f) => (
                    <span key={f} className="text-[10px] px-2 py-1 rounded-full bg-[#f9fcf3] text-[#5c8606] font-medium">{f}</span>
                  ))}
                </div>
                <ul className="mt-3 space-y-1">
                  {p.highlights.slice(0,2).map((h,i) => (
                    <li key={i} className="text-xs text-slate-600 flex gap-1.5"><span className="text-[#70a308]">•</span>{h}</li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-baseline gap-2">
                  <span className="text-xs text-slate-400 line-through">INR {formatINR(p.oldPrice)}</span>
                  <span className="text-lg font-bold text-slate-800">INR {formatINR(p.price)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* POPULAR DESTINATIONS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">EXPLORE THE WORLD</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-800 mt-2">Popular Destinations</h2>
          <p className="mt-3 text-slate-600">
            From misty mountains and tropical beaches to vibrant cities and international favourites, discover destinations selected for every kind of traveller.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {popularDestinations.map((d) => (
            <div key={d.name} className="lift group relative h-80 rounded-2xl overflow-hidden">
              <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h4 className="font-display text-xl font-bold text-white">{d.name}</h4>
                <p className="text-sm text-white/80 mt-1.5 leading-relaxed">{d.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-10">
          <Link to="/destinations">
            <Button className="bg-gradient-to-r from-[#84c009] to-[#4f7700] hover:from-[#70a308] hover:to-[#416200] text-white rounded-full px-7 h-12 font-semibold">
              View All Destinations <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">ABOUT US</div>
        <div className="bg-slate-50 rounded-3xl mt-4 p-8 md:p-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-800 leading-tight">
              Making Every Journey Worth Remembering
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              At Kites Fly, we believe a great holiday begins long before you reach the destination.
            </p>
            <p className="mt-3 text-slate-500 text-sm leading-relaxed">
              It begins with understanding where you want to go, how you want to travel and the experiences you want to bring back home.
            </p>
            <p className="mt-3 text-slate-500 text-sm leading-relaxed">
              Whether you're planning a relaxing family holiday, a romantic honeymoon, a trip with friends or an international adventure, our team helps you plan every detail with care.
            </p>
            <p className="mt-3 text-slate-500 text-sm leading-relaxed">
              From choosing the right destination and accommodation to organising transportation and travel assistance, Kites Fly makes travelling simpler, smoother and more enjoyable.
            </p>
            <p className="mt-5 font-display text-xl md:text-2xl font-semibold italic bg-gradient-to-r from-[#84c009] to-[#4f7700] bg-clip-text text-transparent">
              You dream about the destination. We take care of the journey.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/services">
                <Button className="bg-gradient-to-r from-[#84c009] to-[#4f7700] hover:from-[#70a308] hover:to-[#416200] text-white rounded-full px-6 h-11 font-semibold">Explore Services</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-[#70a308] text-[#5c8606] hover:bg-[#f9fcf3] rounded-full px-6 h-11 font-semibold">Speak to an Expert</Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="bg-white rounded-2xl p-6 border border-slate-100 lift">
                <div className="text-4xl md:text-5xl font-bold text-[#70a308]">{s.value}</div>
                <div className="text-sm text-slate-600 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR VISAS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">VISA SERVICES</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-800 mt-2">Popular Visas</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={() => scrollVisa(-1)} className="w-10 h-10 rounded-full border border-slate-200 hover:border-[#84c009] hover:text-[#5c8606] text-slate-600 flex items-center justify-center transition-colors" aria-label="Prev"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => scrollVisa(1)} className="w-10 h-10 rounded-full border border-slate-200 hover:border-[#84c009] hover:text-[#5c8606] text-slate-600 flex items-center justify-center transition-colors" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
        <div id="visa-scroller" className="no-scrollbar flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4">
          {popularVisas.map((v) => (
            <div key={v.code} className="lift min-w-[220px] max-w-[220px] snap-start bg-white rounded-2xl overflow-hidden border border-slate-100">
              <div className="relative h-40 overflow-hidden">
                <img src={v.flag} alt={v.name} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                <Badge className="absolute bottom-3 left-3 bg-amber-400 text-slate-900 hover:bg-amber-400 rounded-md text-[10px] tracking-wider font-bold">{v.track}</Badge>
              </div>
              <div className="p-4">
                <h4 className="font-bold text-slate-800">{v.name}</h4>
                <p className="text-xs text-slate-500 mt-1">Processing: {v.processing}</p>
                <button className="mt-3 text-xs font-semibold text-[#5c8606] hover:underline flex items-center gap-1">
                  Apply now <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES STRIP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">WHAT WE DO</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-800 mt-2">All travel needs, one roof</h2>
          <p className="mt-3 text-slate-600">From dream planning to safe landing — we take care of every step of your journey.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-10">
          {[
            { Icon: MapPin, title: "Tour Packages", desc: "Customised international & domestic tours." },
            { Icon: Landmark, title: "Pilgrim Packages", desc: "Curated spiritual journeys with comfort." },
            { Icon: BookUser, title: "Passport Services", desc: "New, renewal, tatkal & verification." },
            { Icon: Ticket, title: "Flight, Train & Bus Tickets", desc: "One-stop booking, best fares guaranteed." },
            { Icon: FileCheck, title: "Visa Services", desc: "Hassle-free visa for 100+ countries." },
            { Icon: Hotel, title: "Hotel Reservations", desc: "Handpicked stays worldwide." },
            { Icon: ShieldCheck, title: "Travel Insurance", desc: "Peace of mind on every journey." },
          ].map((s) => (
            <div key={s.title} className="lift group bg-white p-6 rounded-2xl border border-slate-100 hover:border-[#ddedba]">
              <div className="w-12 h-12 rounded-xl bg-[#f9fcf3] text-[#70a308] flex items-center justify-center mb-4 group-hover:bg-gradient-to-r group-hover:from-[#84c009] group-hover:to-[#4f7700] group-hover:text-white transition-colors">
                <s.Icon className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-slate-800">{s.title}</h4>
              <p className="text-sm text-slate-500 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mt-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">WHAT PEOPLE SAY</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-800 mt-2">Testimonials</h2>
            </div>
            <a
              href="https://www.google.com/search?q=kites+fly+travels+kozhikode"
              target="_blank"
              rel="noreferrer"
              className="lift inline-flex items-center gap-4 bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4 hover:border-[#ddedba]"
            >
              <div className="flex items-center gap-1 text-lg font-bold">
                <span className="text-[#4285F4]">G</span>
                <span className="text-[#EA4335]">o</span>
                <span className="text-[#FBBC05]">o</span>
                <span className="text-[#4285F4]">g</span>
                <span className="text-[#34A853]">l</span>
                <span className="text-[#EA4335]">e</span>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                  <span className="ml-1 text-lg font-bold text-slate-800">5.0</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Based on 12 Google Reviews</div>
              </div>
            </a>
          </div>
        </div>
        <div className="relative">
          <div className="flex gap-5 marquee w-max">
            {[...testimonials, ...testimonials].map((t, idx) => (
              <Card key={idx} className="w-[340px] shrink-0 rounded-2xl border-slate-100 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Quote className="w-6 h-6 text-[#84c009]" />
                    <div className="flex items-center gap-1.5 text-xs bg-white border border-slate-100 rounded-md px-2 py-1">
                      <span className="w-2 h-2 rounded-full bg-blue-500" /> Google
                    </div>
                  </div>
                  <Badge className="bg-[#f9fcf3] text-[#5c8606] hover:bg-[#f9fcf3] border border-[#f0f7e1] mb-3">{t.branch}</Badge>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-4">{t.text}</p>
                  <button className="text-xs text-[#5c8606] font-semibold mt-2 hover:underline">Read more</button>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#a6d24e] to-[#5d8c00] text-white font-bold text-xs flex items-center justify-center">{t.initials}</div>
                    <div>
                      <div className="text-sm font-bold text-slate-800">{t.name}</div>
                      <div className="flex items-center gap-2">
                        <div className="flex">{Array.from({length: t.rating}).map((_,i) => <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />)}</div>
                        <span className="text-[10px] text-slate-400">{t.ago}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT / GET IN TOUCH */}
      <section id="contact" className="mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="bg-gradient-to-br from-[#f9fcf3] via-white to-[#f7f9f2] rounded-3xl border border-[#f0f7e1] p-8 md:p-14 grid md:grid-cols-2 gap-10">
            <div>
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.3em] text-[#5c8606] font-semibold">
                <Send className="w-4 h-4" /> GET IN TOUCH
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-800 mt-3">
                Plan Your Next Adventure With Us
              </h2>
              <p className="mt-4 text-slate-600">
                Have questions about visas, holiday packages, or flight bookings? Fill out the form, and our expert travel consultants will get back to you within 24 hours.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                {[
                  { Icon: Phone, label: "CALL US", value: "+91 94963 13677" },
                  { Icon: Mail, label: "EMAIL ADDRESS", value: "kitesflytravels@gmail.com" },
                  { Icon: Clock, label: "OFFICE HOURS", value: "Available 24 x 7" },
                  { Icon: MapPin, label: "LOCATION", value: "Palayam, Kozhikode" },
                ].map((c) => (
                  <div key={c.label} className="bg-white p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#f9fcf3] text-[#70a308] flex items-center justify-center"><c.Icon className="w-5 h-5" /></div>
                    <div>
                      <div className="text-[10px] tracking-widest text-slate-500 font-semibold">{c.label}</div>
                      <div className="text-sm font-semibold text-slate-800">{c.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-800 mb-5">Request a Consultation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Full Name *</label>
                  <Input placeholder="John Doe" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 h-11 rounded-lg" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Phone Number *</label>
                  <Input placeholder="+91 000 000 0000" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-1 h-11 rounded-lg" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Email Address *</label>
                  <Input type="email" placeholder="john@example.com" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1 h-11 rounded-lg" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">City *</label>
                  <Input placeholder="Enter your city" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="mt-1 h-11 rounded-lg" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Travel Destination *</label>
                  <Input placeholder="e.g. Paris, Switzerland" value={form.travel} onChange={(e) => setForm({...form, travel: e.target.value})} className="mt-1 h-11 rounded-lg" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Service Required *</label>
                  <Select value={form.service} onValueChange={(v) => setForm({...form, service: v})}>
                    <SelectTrigger className="h-11 mt-1 rounded-lg"><SelectValue placeholder="Select Service" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tour">Tour Packages</SelectItem>
                      <SelectItem value="pilgrim">Pilgrim Packages</SelectItem>
                      <SelectItem value="passport">Passport Services</SelectItem>
                      <SelectItem value="tickets">Flight, Train &amp; Bus Tickets</SelectItem>
                      <SelectItem value="visa">Visa Services</SelectItem>
                      <SelectItem value="hotel">Hotel Reservations</SelectItem>
                      <SelectItem value="insurance">Travel Insurance</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-slate-600">Message (optional)</label>
                  <Textarea placeholder="Tell us more about your travel plans..." value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="mt-1 min-h-[100px] rounded-lg" />
                </div>
              </div>
              <Button type="submit" className="w-full mt-5 h-12 bg-gradient-to-r from-[#84c009] to-[#4f7700] hover:from-[#70a308] hover:to-[#416200] text-white rounded-lg font-semibold">
                <Send className="w-4 h-4 mr-2" /> Submit Enquiry
              </Button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
